// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute } from '@tanstack/react-router';

// material ui
import {
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Stack,
  Typography,
} from '@mui/material';

// api
import { useGetAccountsQuery } from '@/api/recalc/endpoints/accountsApiSlice';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';

// feature-components
import ServiceManagerDatagrid from '@/features/service-manager/components/ServiceManagerDataGrid';

// components
import { servicesList } from '@/utils/services';

export const Route = createFileRoute('/$accountId/_admin/service-manager/')({
  component: RouteComponent,
});

// Define and export types for the component
export interface Service {
  Id: string | number;
  Name: string;
}

interface ServiceListEntry extends Service {
  path: string;
}

interface ServiceGroup {
  key: string;
  label: string;
  serviceIds: (string | number)[];
}

export interface Account {
  AccountId: string | number;
  Name: string;
  Address: string;
  Services: Service[];
}

export interface ServiceManagerDatagridProps {
  accounts?: Account[];
  isLoading?: boolean;
}

export interface DataGridRow extends Account {
  id: string | number;
}

function RouteComponent() {
  const { t } = useTranslation('service-manager');
  const { data: accounts, isFetching, isLoading } = useGetAccountsQuery();

  const accountList = accounts ?? [];
  const [selectedServiceGroupKeys, setSelectedServiceGroupKeys] = useState<
    string[]
  >([]);

  const serviceGroups = (servicesList as ServiceListEntry[]).reduce(
    (groups, service) => {
      const groupKey = service.path;
      const groupLabel =
        groupKey === 'basic-dashboards' ? 'Basic Dashboards' : service.Name;

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          key: groupKey,
          label: groupLabel,
          serviceIds: [service.Id],
        });
        return groups;
      }

      groups.get(groupKey)?.serviceIds.push(service.Id);
      return groups;
    },
    new Map<string, ServiceGroup>(),
  );

  const groupedServices = Array.from(serviceGroups.values());
  const groupedServiceIdSet = new Map<string, Set<string | number>>(
    groupedServices.map((group) => [group.key, new Set(group.serviceIds)]),
  );

  const filteredAccounts = selectedServiceGroupKeys.length
    ? accountList.filter((account) =>
        account.Services?.some((service) => {
          if (!service?.Id) return false;

          return selectedServiceGroupKeys.some((groupKey) =>
            groupedServiceIdSet.get(groupKey)?.has(service.Id),
          );
        }),
      )
    : accountList;

  const serviceCounts = accountList.reduce((counts, account) => {
    account.Services?.forEach((service) => {
      if (!service?.Id) return;
      counts.set(service.Id, (counts.get(service.Id) || 0) + 1);
    });
    return counts;
  }, new Map<string | number, number>());

  return (
    <PageContainer>
      <PageHeader title={t('title')} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {groupedServices.map((serviceGroup) => {
          const isActive = selectedServiceGroupKeys.includes(serviceGroup.key);
          const serviceCount = serviceGroup.serviceIds.reduce(
            (sum, serviceId) => sum + (serviceCounts.get(serviceId) || 0),
            0,
          );

          return (
            <Grid
              key={serviceGroup.key}
              size={{
                xs: 12,
                sm: 6,
                md: 4,
                lg: 3,
                xl: 2
              }}>
              <Card
                sx={{
                  border: '1px solid',
                  borderColor: isActive ? 'success.main' : 'transparent',
                }}
              >
                <CardActionArea
                  disableRipple
                  onClick={() => {
                    setSelectedServiceGroupKeys((prev) =>
                      prev.includes(serviceGroup.key)
                        ? prev.filter((key) => key !== serviceGroup.key)
                        : [...prev, serviceGroup.key],
                    );
                  }}
                >
                  <CardContent>
                    <Stack spacing={1} alignItems="center">
                      <Typography
                        variant="overline"
                        color={isActive ? 'text.primary' : 'text.secondary'}
                      >
                        {serviceGroup.label}
                      </Typography>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        color={isActive ? 'text.primary' : 'text.secondary'}
                      >
                        {serviceCount}
                      </Typography>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <ServiceManagerDatagrid
        accounts={filteredAccounts}
        isLoading={isLoading || isFetching}
      />
    </PageContainer>
  );
}
